import { useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useTenant } from "@/contexts/TenantContext";
import { toast } from "sonner";

interface ProrationCardPaymentProps {
  toPlanId: string;
  amount: number;
  onSuccess: () => void;
  prorationMeta?: any;
}

export function ProrationCardPayment({ toPlanId, amount, onSuccess, prorationMeta }: ProrationCardPaymentProps) {
  const { tenantId } = useTenant();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [cpf, setCpf] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [cardholderName, setCardholderName] = useState("");
  const [expiration, setExpiration] = useState("");
  const [cvv, setCvv] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const formatCard = (v: string) => v.replace(/\D/g, "").slice(0,19).replace(/(.{4})/g,"$1 ").trim();
  const formatCPF = (v: string) => v.replace(/\D/g,"").slice(0,11).replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, (_,$1,$2,$3,$4)=> [$1,$2,$3,$4].filter(Boolean).join($4?"-":"."));
  const formatExp = (v: string) => {
    const d = v.replace(/\D/g,"").slice(0,4);
    const mm = d.slice(0,2);
    const yy = d.slice(2,4);
    const clamped = mm ? String(Math.min(Math.max(parseInt(mm||"0",10),1),12)).padStart(2,'0') : "";
    return yy ? `${clamped}/${yy}` : clamped;
  };
  const validEmail = useMemo(()=> /.+@.+\..+/.test(email), [email]);
  const cpfDigits = useMemo(()=> cpf.replace(/\D/g, ""), [cpf]);
  const validCPF = useMemo(()=> {
    const s = cpfDigits; if (s.length!==11 || /^(\d)\1+$/.test(s)) return false;
    const calc = (len: number) => { let sum=0; for(let i=0;i<len;i++){ sum+= parseInt(s.charAt(i))*((len+1)-i); } const mod = (sum*10)%11; return mod===10?0:mod; };
    return calc(9)===parseInt(s.charAt(9)) && calc(10)===parseInt(s.charAt(10));
  }, [cpfDigits]);
  const expParts = useMemo(()=> { const [mm,yy] = (expiration||"").split('/'); return {mm,yy}; }, [expiration]);
  const validExp = useMemo(()=> {
    const {mm,yy} = expParts; if(!mm || !yy) return false; const m=parseInt(mm,10); if(m<1||m>12) return false; const year=2000+parseInt(yy,10); const now=new Date(); const expDate=new Date(year, m, 0); return expDate>= new Date(now.getFullYear(), now.getMonth(), 1);
  }, [expParts]);
  const cardDigits = useMemo(()=> cardNumber.replace(/\D/g,"") , [cardNumber]);
  const validCvv = useMemo(()=> /^\d{3,4}$/.test(cvv), [cvv]);

  const mpStatusMessages: Record<string,string> = {
    cc_rejected_bad_filled_card_number: "Número do cartão inválido",
    cc_rejected_bad_filled_other: "Dados do cartão inválidos",
    cc_rejected_bad_filled_date: "Data de validade inválida",
    cc_rejected_bad_filled_security_code: "CVV inválido",
    cc_rejected_insufficient_amount: "Saldo/limite insuficiente",
    cc_rejected_call_for_authorize: "Autorize com seu banco",
    cc_rejected_card_disabled: "Cartão desativado",
    cc_rejected_card_high_risk: "Pagamento recusado por segurança",
    cc_rejected_blacklist: "Cartão bloqueado",
    cc_rejected_max_attempts: "Máximo de tentativas excedido",
    cc_rejected_other_reason: "Pagamento recusado",
  };

  const submit = async () => {
    setErrorMsg(null);
    if (!tenantId) { setErrorMsg("Tenant não identificado"); return; }
    if (!validEmail) { setErrorMsg("Email inválido"); return; }
    if (!validCPF) { setErrorMsg("CPF inválido"); return; }
    const [mm, yy] = (expiration || "").split("/");
    if (!mm || !yy || !validExp) { setErrorMsg("Validade inválida"); return; }
    if (cardDigits.length < 13 || cardDigits.length > 19) { setErrorMsg("Número de cartão inválido"); return; }
    if (!validCvv) { setErrorMsg("CVV inválido"); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-mercadopago-card-payment', {
        body: {
          tenant_id: tenantId,
          amount: Number(amount || 0),
          description: 'Prorrata de upgrade de plano',
          to_plan_id: toPlanId,
          email,
          cpf: String(cpf).replace(/\D/g, ''),
          card_number: String(cardNumber).replace(/\s/g, ''),
          cardholder_name: cardholderName,
          expiration_month: Number(mm),
          expiration_year: Number(`20${yy}`),
          security_code: cvv,
          proration_meta: prorationMeta || undefined,
        },
      });
      if (error || !data?.success) throw new Error(data?.error || 'Falha no pagamento');
      const status: string = data?.status;
      const detail: string = data?.status_detail;
      if (status === 'approved') {
        toast.success('Pagamento aprovado!');
        onSuccess();
      } else if (status === 'pending' || status === 'in_process') {
        toast.info('Pagamento em análise. Aplicaremos o upgrade quando for aprovado.');
      } else if (status === 'rejected') {
        const msg = mpStatusMessages[detail] || 'Pagamento recusado';
        setErrorMsg(msg);
        toast.error(msg);
      } else {
        toast.info('Pagamento recebido com status: ' + (status || 'desconhecido'));
      }
    } catch (e: any) {
      const msg = String(e?.message || 'Erro ao pagar');
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Alert>
        <AlertDescription>Pagamento com cartão de crédito da diferença proporcional para ativar o novo plano imediatamente.</AlertDescription>
      </Alert>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Input placeholder="Email" value={email} onChange={(e)=> setEmail(e.target.value)} />
        <Input placeholder="CPF" value={cpf} onChange={(e)=> setCpf(formatCPF(e.target.value))} />
        <Input placeholder="Número do Cartão" value={cardNumber} onChange={(e)=> setCardNumber(formatCard(e.target.value))} />
        <Input placeholder="Nome impresso" value={cardholderName} onChange={(e)=> setCardholderName(e.target.value)} />
        <Input placeholder="Validade MM/AA" value={expiration} onChange={(e)=> setExpiration(formatExp(e.target.value))} />
        <Input placeholder="CVV" value={cvv} onChange={(e)=> setCvv(e.target.value.replace(/\D/g,'').slice(0,4))} />
      </div>
      {errorMsg && (
        <Alert variant="destructive"><AlertDescription>{errorMsg}</AlertDescription></Alert>
      )}
      <Button className="w-full" onClick={submit} disabled={loading}>
        {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Pagando...</>) : `Pagar ${new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(amount || 0)}`}
      </Button>
    </div>
  );
}
